import React, { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Product, ProductVariant, ProductType, WeightUnit } from "../types/api";
import {
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  IconButton,
} from "@mui/material";
import { Edit as EditIcon, Clear as ClearIcon } from "@mui/icons-material";
import { Box } from "@mui/system";
import { ProductsRoute, ViewProductRoute } from "src/routes";
import { useForm } from "react-hook-form";
import { SupplierPicker } from "src/components/supplier-picker.component";
import ProductVariantTable from "src/components/productvariant-table.component";
import _ from "lodash";
import { createProduct, updateProduct } from "src/services/product.service";
import GlobalMessageContext from "src/context/globalMessage.context";
import { LocationPicker } from "src/components/location-picker.component";
import { sanitizeProductField } from "src/utils/html-sanitizer";
import { hasDuplicateFilterValues } from "src/utils/variant-utils";
import { endSaleOnVariant, isVariantOnSale, syncSaleTag } from "src/utils/sale-utils";
import TagChips from "src/components/tag-chips";
import { TagSelectorModal } from "src/components/modal.tag-selector";
import WysiwygEditor from "src/components/wysiwyg-editor.component";

const EditProductView = () => {
  const { palette } = useTheme();
  const {
    register,
    setError,
    setValue,
    clearErrors,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm();

  const uxMessage = useContext(GlobalMessageContext);

  const { state } = useLocation();
  const createNew = state["createNew"];
  const initialProduct: Product = {
    name: "",
    brand: "",
    description: "",
    tags: [],
    variants: [],
    productType: undefined as any,
    taxable: true,
  };

  const [product, setProduct] = useState<Product>(
    createNew ? initialProduct : state["selectedProduct"]
  );


  const [tagSelectorOpen, setTagSelectorOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [confirmSaveDialogOpen, setConfirmSaveDialogOpen] = useState(false);
  // Saving (including the Shopify update) can take a while. The screen is
  // blocked until it finishes so the user can't wander off mid-save, and on
  // failure they stay on the form with their edits intact.
  const [saving, setSaving] = useState(false);
  // If the user does leave (e.g. the browser back button), don't drag them
  // back to the product view when the save completes.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  // Kilogram check: almost nothing we sell is heavier than a kilo, and a
  // weight typed in grams with the unit left on Kg (a "200 Kg" songbook) gets
  // pushed to Shopify and trips the heavy-parcel shipping rate. So any
  // variant in Kg is confirmed explicitly before we go on. `kgDialogNext`
  // is what to do once confirmed: the normal Shopify confirmation, or a
  // straight draft save from the duplicates dialog.
  const [kgDialogNext, setKgDialogNext] = useState<null | "confirm" | "save">(null);

  // Raw text backing the "Variant Options" input. Kept separate from the
  // cleaned `product.filterFields` array so that typing a comma (to start a
  // second option) isn't immediately stripped away when the array drops the
  // resulting empty segment.
  const [filterFieldsText, setFilterFieldsText] = useState<string>(() => {
    const src = createNew ? initialProduct : state["selectedProduct"];
    const ff = src?.filterFields;
    return Array.isArray(ff) ? ff.join(",") : (ff as string) || "";
  });

  const productName = createNew ? (
    <>New</>
  ) : (
    <>
      <i>Editing:</i> {product.name}
    </>
  );

  const _productTypeChanged = (data: any, input: string) => {
    const type = data.target.value;
    setProduct({
      ...product,
      productType: type,
      taxable: type !== ProductType.BOOK,
    });
  };

  const _supplierChanged = (event: any, supplier: any) => {
    setProduct({ ...product, supplierGuid: supplier?.guid || "" });
  };

  const _locationChanged = (location: string | null) => {
    setProduct({ ...product, location: location || "Unknown" });
  };

  const _tagsChanged = (selectedTags: string[]) => {
    // Preserve any existing non-navigation tags
    const existingNonNavTags = product.tags?.filter(tag => 
      !selectedTags.includes(tag) && tag !== ""
    ) || [];
    
    // Combine selected navigation tags with existing non-navigation tags
    const allTags = [...selectedTags, ...existingNonNavTags]
      .filter(tag => tag && tag.trim() !== "")
      .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates

    setProduct({ ...product, tags: allTags });
    setTagSelectorOpen(false);
  };

  const _openTagSelector = () => {
    setTagSelectorOpen(true);
  };

  const _closeTagSelector = () => {
    setTagSelectorOpen(false);
  };

  const _clearTags = () => {
    setProduct({ ...product, tags: [] });
  };

  const _deleteIndividualTags = (tagsToDelete: string[]) => {
    const updatedTags = product.tags?.filter(tag => !tagsToDelete.includes(tag)) || [];
    setProduct({ ...product, tags: updatedTags });
  };

  const _filterFieldsChanged = (e: ChangeEvent<HTMLInputElement>) => {
    const newFilterFields = e.target.value.replace(/\s/g, "");
    // Keep the raw text (including any trailing comma) for the input display so
    // the user can type "Colour," and then start the second option.
    setFilterFieldsText(newFilterFields);
    const filterFieldsArray = newFilterFields.split(",").filter(f => f.trim() !== "");

    // Update existing variants to have complete filterValues
    const updatedVariants = product.variants.map((variant, index) => {
      if (!variant.filterValues || variant.filterValues.length === 0 || 
          (Array.isArray(variant.filterValues) && variant.filterValues.some(v => !v || v.trim() === "")) ||
          (!Array.isArray(variant.filterValues) && variant.filterValues === "")) {
        
        // Generate default values for this variant
        const defaultValues = filterFieldsArray.map((_field, optionIndex) => 
          optionIndex === 0 ? "Default" : `Default ${optionIndex + 1}`
        );
        
        console.log(`Updating variant ${index + 1} (SKU: ${variant.sku}) with default filterValues: ${defaultValues.join(', ')}`);
        
        return {
          ...variant,
          filterValues: defaultValues
        };
      }
      
      // If variant has some values but not enough, pad with defaults
      let existingValues: string[];
      if (Array.isArray(variant.filterValues)) {
        existingValues = variant.filterValues;
      } else if (typeof variant.filterValues === 'string' && variant.filterValues) {
        existingValues = (variant.filterValues as string).split(',');
      } else {
        existingValues = [];
      }
          
      if (existingValues.length < filterFieldsArray.length) {
        const paddedValues = [...existingValues];
        for (let i = existingValues.length; i < filterFieldsArray.length; i++) {
          paddedValues.push(i === 0 ? "Default" : `Default ${i + 1}`);
        }
        
        console.log(`Padding variant ${index + 1} (SKU: ${variant.sku}) filterValues: ${paddedValues.join(', ')}`);
        
        return {
          ...variant,
          filterValues: paddedValues
        };
      }
      
      return variant;
    });
    
    setProduct({ ...product, filterFields: filterFieldsArray, variants: updatedVariants });
  };

  const _valueChanged = (e: ChangeEvent<HTMLInputElement>) => {
    const key = e.target.id;
    const value = e.target.value;

    // Sanitize name field (description is now handled by WYSIWYG editor)
    const sanitizedValue = key === 'name' 
      ? sanitizeProductField(value) 
      : value;

    setProduct({ ...product, [key]: sanitizedValue });
  };

  const _descriptionChanged = (html: string) => {
    // Sanitize HTML content from WYSIWYG editor
    const sanitizedDescription = sanitizeProductField(html);
    
    // Use functional state update to avoid stale closure issues
    setProduct(prevProduct => ({ ...prevProduct, description: sanitizedDescription }));
    
    // Update form validation for react-hook-form
    setValue('description', sanitizedDescription);
    if (sanitizedDescription.trim()) {
      clearErrors('description');
    }
  };

  const _addVariant = () => {
    const filterFieldsStr = Array.isArray(product.filterFields) 
      ? product.filterFields.join(",") 
      : product.filterFields || "";
    
    if (product.variants?.length > 0 && (!product.filterFields || filterFieldsStr.trim() === "")) {
      product.filterFields = ["Variant"];
      setValue("filterFields", "Variant");
      setFilterFieldsText("Variant");
      
      // Ensure existing variants get default filterValues
      product.variants.forEach((variant, index) => {
        if (!variant.filterValues || variant.filterValues.length === 0) {
          variant.filterValues = ["Default"];
          console.log(`Updated existing variant ${index + 1} (SKU: ${variant.sku}) with default filterValues: Default`);
        }
      });
    }

    // Use the last variant as a template, if there is one
    const tempSku = `temp-${new Date().getUTCMilliseconds() % 10000}`;
    const v: ProductVariant = {
      product: product,
      retailPrice: 0,
      previousPrice: 0,
      buyPrice: 0,
      movingAverageCost: 0,
      weight: 0,
      manageStockLevels: true,
      stockOnHand: 0,
      // Grams by default: almost everything we sell is under a kilo, and a
      // Kg default is how "200 Kg" songbooks were getting into the catalogue.
      weightUnit: WeightUnit.G,
      sku: tempSku,
      supplierCode: "",
      sellable: true,
      buyable: true,
    };

    v.guid = undefined;

    // make the id -ve for new variants so we know to rip it out on save
    v.id = -(new Date().getUTCMilliseconds() % 1000);

    // base the SKU on the first variant, if there is one, and copy some other fields for convenience
    if (!_.isEmpty(product.variants)) {
      const firstSku = product.variants[0].sku.split("-")[0];
      v.sku = `${firstSku}-${tempSku.substring("temp-".length)}`;
      v.retailPrice = product.variants[product.variants.length - 1].retailPrice;
      v.buyPrice = product.variants[product.variants.length - 1].buyPrice;
      v.movingAverageCost = v.buyPrice;
      v.previousPrice = product.variants[product.variants.length - 1].previousPrice;
      v.weight = product.variants[product.variants.length - 1].weight;
      v.weightUnit = product.variants[product.variants.length - 1].weightUnit;
    }

    v.imageUrls = [];
    const fields = product.filterFields
      ? (Array.isArray(product.filterFields) 
          ? product.filterFields 
          : (product.filterFields as string).split(/\s*,\s*/))
      : [];
    v.filterValues = fields.map((_f, i) => i === 0 ? "Default" : `Default ${i + 1}`);

    product.variants.push(v);

    setProduct({ ...product, variants: product.variants, filterFields: product.filterFields });
  };

  if (createNew && _.isEmpty(product.variants)) {
    _addVariant();
  }

  const navigate = useNavigate();
  const _save = () => {
    if (_checkProduct(product)) {
      // Empty imageURL arrays throw the server, so remove these if we have any
      product.variants.forEach((v) => {
        if (_.isEmpty(v.imageUrls)) {
          v.imageUrls = undefined;
        }
      });

      product.tags = product.tags
        .filter((t) => !_.isEmpty(t))
        .filter((value, index, self) => {
          return self.indexOf(value) === index;
        });

      const fn = createNew ? createProduct : updateProduct;

      setSaving(true);
      fn({
        product: product,
        success: (r) => {
          setSaving(false);
          uxMessage.setMessage({
            message: `Product ${r.data.name} ${
              createNew ? "created" : "updated"
            } successfully`,
            severity: "success",
          });

          if (!mounted.current) return;
          navigate(ViewProductRoute, {
            state: { selectedProduct: r.data },
          });
        },
        failure: (e) => {
          setSaving(false);
          uxMessage.setMessage({
            message: e,
            severity: "error",
          });
        },
      });
    }
  };

  // Runs after react-hook-form field validation passes. Decides which
  // confirmation dialog to show: if the product has duplicate variant
  // combinations it can't be published to Shopify, so we warn about that up
  // front (before the "this will update Shopify" confirmation).
  const _onSubmitAttempt = () => {
    if (hasDuplicateFilterValues(product)) {
      setDuplicateDialogOpen(true);
      return;
    }
    _afterKgCheck("confirm");
  };

  const variantsInKg = (product.variants || []).filter(
    (v: any) => v.pendingDelete !== true && v.weightUnit === WeightUnit.KG
  );

  // Runs `next` straight away unless a variant is in Kg, in which case the
  // Kg dialog runs it on confirmation.
  const _afterKgCheck = (next: "confirm" | "save") => {
    if (variantsInKg.length > 0) {
      setKgDialogNext(next);
      return;
    }
    if (next === "save") _save();
    else setConfirmSaveDialogOpen(true);
  };

  const _kgConfirmed = () => {
    const next = kgDialogNext;
    setKgDialogNext(null);
    if (next === "save") _save();
    else setConfirmSaveDialogOpen(true);
  };

  const variantsOnSale = (product.variants || []).filter(
    (v: any) => v.pendingDelete !== true && isVariantOnSale(v)
  ).length;

  // Ends the sale on every variant at once. Same local-edit semantics as the
  // per-row action: nothing is saved until Save is pressed.
  const _endSaleOnAllVariants = () => {
    const next = { ...product, variants: product.variants.map((v) => ({ ...v })) };
    next.variants.forEach((v: any) => {
      if (v.pendingDelete !== true) endSaleOnVariant(v);
    });
    next.tags = [...(next.tags || [])];
    syncSaleTag(next);
    _setProductWithChecks(next);
  };

  const _setProductWithChecks = (p: Product) => {
    _checkProduct(p);
    setProduct(p);
  };

  const _checkProduct = (p: Product): boolean => {
    clearErrors("variants");
    clearErrors("tags");

    // Tags are now optional - no validation required

    // Ensure we have at least 1 variant
    if (!p.variants || _.isEmpty(p.variants)) {
      setError("variants", { message: "Please create at least one variant" });
      return false;
    }

    // Check for duplicate SKUs
    const uniqueSkus =
      p.variants
        .filter((v: any) => !v.pendingDelete)
        .map((v) => v.sku)
        .filter((v, i, s) => s.indexOf(v) === i).length ===
      p.variants.filter((v: any) => !v.pendingDelete).length;

    // Check for duplicate supplier codes (where they're not empty)
    const variantsWithSupplierCodes = p.variants.filter(
      (v) => v.supplierCode && v.supplierCode.trim() !== ""
    );

    const uniqueSupplierCodes =
      variantsWithSupplierCodes
        .filter((v: any) => !v.pendingDelete)
        .map((v) => v.supplierCode)
        .filter((v, i, s) => s.indexOf(v) === i).length ===
      variantsWithSupplierCodes.filter((v: any) => !v.pendingDelete).length;

    if (!uniqueSupplierCodes) {
      setError("variants", {
        message: "Where supplier codes are provided they must be unique",
      });
      return false;
    }

    // Check we have filter fields if we have multiple variants.
    const filterFieldsStr = Array.isArray(product.filterFields) 
      ? product.filterFields.join(",") 
      : product.filterFields || "";
    
    if (
      product.variants.filter((v: any) => !v.pendingDelete).length > 1 &&
      (!product.filterFields || filterFieldsStr.trim() === "")
    ) {
      setError("variants", {
        message:
          "You must set up variant options if there are multiple variants",
      });
      return false;
    }

    if (!uniqueSkus) {
      setError("variants", { message: "Please ensure SKUs are unique" });
      return false;
    }

    // Check that each variant has a unique combination of filter values.
    // Shopify rejects duplicate option combinations, so a product in this
    // state must never be published. Duplicates are surfaced via the red cell
    // highlighting in the variant table and a dialog shown when Save is
    // clicked (see _onSubmitAttempt), not an inline form error. We still allow
    // an unpublished product to be saved as a draft, but a product that is
    // already published cannot be saved while duplicates exist.
    if (hasDuplicateFilterValues(p) && p.publishedToShopify) {
      return false;
    }

    return true;
  };

  return (
    <>
      <Backdrop
        open={saving}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          color: "#fff",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">
          {createNew ? "Creating" : "Saving"} {product.name}...
        </Typography>
      </Backdrop>
      <Box sx={{ m: 1 }}>
        <Grid container>
          <Grid size={12}>
            <Typography variant="h2" align="left">
              <Link to={ProductsRoute}>Products</Link>
              &nbsp;&gt;&nbsp;{productName}
            </Typography>
            <Divider sx={{ margin: 1 }} />
          </Grid>
        </Grid>
        <form onSubmit={handleSubmit((d) => _save())}>
          <Grid container spacing={2}>
            <Grid size={2}>
              {/* {product.variants[0]?.imageUrls[0]?.uri && (
                <ImageView
                  uri={product.variants[0].imageUrls[0].uri}
                  alt={product.name}
                />
              )} */}
            </Grid>
            <Grid size={12}>
              <Grid container spacing={2}>
                <Grid size={12} />
                <Grid size={2}>
                  <FormControl fullWidth error={'productType' in errors}>
                    <InputLabel>Product Type *</InputLabel>
                    <Select
                      {...register("productType", { 
                        required: createNew,
                        validate: (value) => {
                          if (createNew && (!value || value === ProductType.OTHER)) {
                            return "Please select a specific product type. 'Other' is not allowed.";
                          }
                          return true;
                        }
                      })}
                      label="Product Type"
                      value={product.productType || ""}
                      sx={{ textAlign: "left" }}
                      onChange={_productTypeChanged}
                      disabled={!createNew}
                    >
                      <MenuItem value="none" disabled>Please select...</MenuItem>
                      <MenuItem key={ProductType.BOOK} value="Book">
                        Book
                      </MenuItem>
                      <MenuItem key={ProductType.ACCESSORY} value="Accessory">
                        Accessory
                      </MenuItem>
                      <MenuItem key={ProductType.GIFT} value="Gift">
                        Gift
                      </MenuItem>
                      <MenuItem key={ProductType.INSTRUMENT} value="Instrument">
                        Instrument
                      </MenuItem>
                      <MenuItem key={ProductType.OTHER} value="Other">
                        Other
                      </MenuItem>
                    </Select>
                    {errors.productType && (
                      <FormHelperText>
                        {String(errors.productType.message) || "You must select a product type from the list."}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid size={1}>
                  <TextField
                    id="taxable"
                    fullWidth
                    value={!product.taxable ? "No" : "Yes"}
                    label={"Taxable?"}
                  />
                </Grid>
                <Grid size={6}>
                  <SupplierPicker
                    register={register}
                    errors={errors}
                    value={product?.supplierGuid ? product.supplierGuid : ""}
                    supplierChanged={_supplierChanged}
                    setValue={setValue}
                    trigger={trigger}
                  />
                </Grid>
                <Grid size={3}>
                  <TextField
                    id="brand"
                    fullWidth
                    value={product.brand}
                    label="Product brand *"
                    {...register("brand", { required: true })}
                    onChange={_valueChanged}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    id="name"
                    fullWidth
                    value={product.name}
                    label="Product name *"
                    {...register("name", { required: true })}
                    onChange={_valueChanged}
                  />
                </Grid>
                <Grid size={12}>
                  <WysiwygEditor
                    defaultValue={product.description || ""}
                    onChange={_descriptionChanged}
                    label="Product description *"
                    error={!!errors.description}
                    helperText={errors.description?.message as string}
                    placeholder="Enter a detailed product description..."
                    data-testid="product-description-editor"
                    productData={{
                      name: product.name,
                      productType: product.productType,
                      variants: product.variants
                    }}
                    showAIButton={true}
                  />
                </Grid>
                <Grid size={4}>
                  <LocationPicker
                    value={product?.location ? product.location : "Unknown"}
                    onChange={_locationChanged}
                    allowNew
                    required
                    helperText="Choose a location, or type a name to add a new one"
                  />
                </Grid>
                <Grid size={4}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    minHeight: '56px',
                    border: '1px solid',
                    borderColor: errors.tags ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
                    borderRadius: 1,
                    p: 1,
                    '&:hover': {
                      borderColor: errors.tags ? 'error.main' : 'text.primary',
                    },
                    '&:focus-within': {
                      borderColor: errors.tags ? 'error.main' : 'primary.main',
                      borderWidth: '2px',
                      p: 'calc(4px - 1px)',
                    }
                  }}>
                    <Box sx={{ flex: 1 }}>
                      {product.tags && product.tags.length > 0 ? (
                        <TagChips 
                          tags={product.tags} 
                          marginRight="0.5em"
                          onDelete={_deleteIndividualTags}
                        />
                      ) : (
                        <Typography variant="body1" color="text.secondary">
                          No tags selected
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton 
                        onClick={_openTagSelector}
                        size="small"
                        title="Edit tags"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        onClick={_clearTags}
                        size="small"
                        title="Clear all tags"
                        disabled={!product.tags || product.tags.length === 0}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <FormHelperText>
                    {errors.tags ? String(errors.tags.message || '') : "Click edit to select tags or clear to remove all"}
                  </FormHelperText>
                </Grid>
                <Grid size={4}>
                  <TextField
                    id="filterFields"
                    fullWidth
                    value={filterFieldsText}
                    label={
                      product.variants.filter((v: any) => !v.pendingDelete).length >
                      1
                        ? "Variant Options *"
                        : "Variant Options"
                    }
                    {...register("filterFields", {
                      required:
                        product.variants.filter((v: any) => !v.pendingDelete)
                          .length > 1,
                    })}
                    onChange={_filterFieldsChanged}
                  />
                  <FormHelperText>
                    Please separate variant options with commas
                  </FormHelperText>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}></Grid>
            <Grid size={3}>
              <Button
                color="secondary"
                size="small"
                variant="contained"
                onClick={() => {
                  if (createNew) {
                    navigate(ProductsRoute);
                  } else {
                    navigate(ViewProductRoute, {
                      state: { selectedProduct: product },
                    });
                  }
                }}
                sx={{ float: "left" }}
              >
                Cancel
              </Button>
            </Grid>
            <Grid size={6}>
              {product && product.publishedToShopify && (
                <Typography variant="h6">
                  Note: product is published to Shopify
                </Typography>
              )}
              {createNew && (
                <Typography variant="h6">
                  Note: SKUs are created on first save, and may then be edited.
                </Typography>
              )}
              {errors.variants && (
                <Typography variant="h6" sx={{ color: palette.error.main }}>
                  Error: {String(errors.variants.message || '')}
                </Typography>
              )}
            </Grid>
            <Grid size={3} sx={{ textAlign: "right" }}>
              <Tooltip title={createNew ? "Save the product before adding variants" : ""}>
                <span>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    sx={{ marginRight: 2 }}
                    onClick={() => _addVariant()}
                    disabled={createNew}
                  >
                    Add Variant
                  </Button>
                </span>
              </Tooltip>
              <Button
                color="primary"
                size="small"
                variant="contained"
                onClick={handleSubmit(() => _onSubmitAttempt())}
              >
                {createNew ? "Create" : "Save"}
              </Button>
            </Grid>
            <Grid size={12} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="h2">Variants</Typography>
              {variantsOnSale >= 2 && (
                <Button
                  variant="outlined"
                  size="small"
                  color="inherit"
                  onClick={_endSaleOnAllVariants}
                  data-testid="end-sale-all"
                >
                  {`End sale on all ${variantsOnSale} variants`}
                </Button>
              )}
            </Grid>
            <Grid size={12}>
              <ProductVariantTable
                parentProduct={product}
                setParentProduct={(p: Product) =>
                  _setProductWithChecks({ ...p })
                }
                editMode={true}
              />
            </Grid>
          </Grid>
        </form>
        
        <TagSelectorModal
          open={tagSelectorOpen}
          onClose={_closeTagSelector}
          onSave={_tagsChanged}
          currentTags={product.tags}
        />

        {/* Shown when the product has duplicate variant combinations, which
            block publishing to Shopify. */}
        <Dialog
          open={duplicateDialogOpen}
          onClose={() => setDuplicateDialogOpen(false)}
        >
          <DialogTitle>Duplicate variant combinations</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {product.publishedToShopify
                ? "Two or more variants share the same combination of option values (highlighted in red). Shopify does not allow duplicate variant combinations, so this published product cannot be saved until the duplicates are resolved."
                : "Two or more variants share the same combination of option values (highlighted in red). This product cannot be published to Shopify until the duplicates are resolved. You can still save it as a draft and finish setting it up later."}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            {product.publishedToShopify ? (
              <Button
                color="primary"
                variant="contained"
                onClick={() => setDuplicateDialogOpen(false)}
              >
                Ok
              </Button>
            ) : (
              <>
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={() => setDuplicateDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    setDuplicateDialogOpen(false);
                    _afterKgCheck("save");
                  }}
                >
                  Save as draft
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Shown when any variant's weight is in kilograms. */}
        <Dialog open={kgDialogNext !== null} onClose={() => setKgDialogNext(null)}>
          <DialogTitle>Weight in kilograms?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {variantsInKg.length === 1
                ? "This variant has its weight in Kg. Is this right?"
                : "These variants have their weight in Kg. Is this right?"}
            </DialogContentText>
            <ul data-testid="kg-variant-list">
              {variantsInKg.map((v: any) => (
                <li key={v.sku}>
                  <strong>{v.sku}</strong>: {v.weight}Kg
                  {v.weight >= 1 && ` (${(v.weight * 1000).toLocaleString()}g)`}
                </li>
              ))}
            </ul>
            <DialogContentText>
              {variantsInKg.length === 1
                ? "If it should be grams, please cancel and change the unit in the Variants table."
                : "If they should be grams, please cancel and change the unit in the Variants table."}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              color="secondary"
              variant="contained"
              onClick={() => setKgDialogNext(null)}
              data-testid="kg-cancel"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={_kgConfirmed}
              data-testid="kg-confirm"
            >
              Yes, it is in Kg
            </Button>
          </DialogActions>
        </Dialog>

        {/* Standard save confirmation, shown when there are no duplicates. */}
        <Dialog
          open={confirmSaveDialogOpen}
          onClose={() => setConfirmSaveDialogOpen(false)}
        >
          <DialogTitle>Are you sure?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {product.publishedToShopify
                ? "This product is published to Shopify so this will update the product and all variants in AMS and Shopify!"
                : "This will update all product and variant details!"}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              color="secondary"
              variant="contained"
              onClick={() => setConfirmSaveDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                setConfirmSaveDialogOpen(false);
                _save();
              }}
            >
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default EditProductView;
